import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, NAV_SECTION_PATHS } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, AlertOctagon, AlertTriangle, Info, TrendingUp, RefreshCw, Loader2, ExternalLink, CheckCircle2, ArrowRight } from "lucide-react";

export const SEV_META = {
  critical: { label: "Critical", icon: AlertOctagon, cls: "text-rose-700 bg-rose-50 ring-rose-200" },
  high: { label: "High priority", icon: AlertTriangle, cls: "text-amber-700 bg-amber-50 ring-amber-200" },
  warning: { label: "Warnings", icon: Info, cls: "text-sky-700 bg-sky-50 ring-sky-200" },
  opportunity: { label: "Sales opportunities", icon: TrendingUp, cls: "text-emerald-700 bg-emerald-50 ring-emerald-200" },
};

export function useBriefing(params) {
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const load = useCallback(() => {
    api.get("/ops-briefing", { params }).then((r) => setData(r.data)).catch((e) => { if (e.response?.status === 403) setDenied(true); else setData({ items: [], counts: {} }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(params)]);
  useEffect(() => { load(); }, [load]);
  return { data, denied, load };
}

export function RiskRow({ r, onAck, compact }) {
  const { navHidden } = useAuth();
  const blocked = (to) => [...(navHidden || [])].some((k) => (NAV_SECTION_PATHS[k] || []).some((p) => to === p || to.startsWith(`${p}/`)));
  const m = SEV_META[r.severity];
  const ex = r.explanation || {};
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3.5 transition-shadow hover:shadow-sm" data-testid={`risk-${r.id}`}>
      <div className="flex items-start gap-2.5">
        <m.icon className={`mt-0.5 h-4 w-4 shrink-0 ${m.cls.split(" ")[0]}`} />
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold text-slate-900">{r.title}</div>
          <div className="mt-0.5 text-xs text-slate-500">{r.department}{r.owner ? ` \u00b7 ${r.owner}` : ""}{r.acknowledged ? " \u00b7 acknowledged" : ""}</div>
          {!compact && (
            <div className="mt-2 space-y-1 text-sm text-slate-700" data-testid={`risk-explanation-${r.id}`}>
              <p><span className="font-medium text-slate-900">Why it matters:</span> {ex.why}</p>
              <p><span className="font-medium text-slate-900">Impact:</span> {ex.impact}</p>
              <p><span className="font-medium text-slate-900">Next action:</span> {ex.action}</p>
              <p className="text-[11px] uppercase tracking-wide text-slate-400">{ex.source === "ai" ? "AI recommendation from the linked records" : "Rule-based summary"}</p>
            </div>
          )}
          {r.data_gaps?.length > 0 && <div className="mt-1.5 text-xs text-amber-700" data-testid={`risk-gaps-${r.id}`}>Incomplete data: {r.data_gaps.join("; ")}</div>}
          <div className="mt-2 flex flex-wrap items-center gap-2">
            {r.records.map((x) => blocked(x.link) ? (
              <span key={`${x.type}-${x.id}`} className="rounded-full bg-slate-50 px-2 py-0.5 text-xs text-slate-400" title="Your role cannot open this page" data-testid={`risk-ref-${r.id}-${x.type}-${x.id}`}>{x.label}</span>
            ) : (
              <Link key={`${x.type}-${x.id}`} to={x.link} className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-700 hover:bg-indigo-50 hover:text-indigo-700" data-testid={`risk-link-${r.id}-${x.type}-${x.id}`}>
                {x.label} <ExternalLink className="h-3 w-3" />
              </Link>
            ))}
            {onAck && !r.acknowledged && (
              <button onClick={() => onAck(r.id)} className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-emerald-700" data-testid={`risk-ack-${r.id}`}>
                <CheckCircle2 className="h-3.5 w-3.5" /> Acknowledge
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function OpsBriefingCard() {
  const { data, denied, load } = useBriefing({});
  const [busy, setBusy] = useState(false);
  if (denied || data?.disabled) return null;
  const top = (data?.items || []).filter((r) => r.severity !== "opportunity").slice(0, 4);
  const refresh = async () => { setBusy(true); try { await api.post("/ops-briefing/refresh"); load(); } catch { toast.error("Could not refresh"); } finally { setBusy(false); } };
  return (
    <div className="h-full rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="ops-briefing-card">
      <div className="mb-4 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-indigo-50 text-indigo-600"><Sparkles className="h-4 w-4" /></span>
          <h2 className="text-base font-bold text-slate-900">Operations Briefing</h2>
        </div>
        <button onClick={refresh} className="rounded-full border border-slate-200 p-1.5 text-slate-500 hover:text-indigo-700" data-testid="ops-briefing-refresh">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </button>
      </div>
      {!data ? <div className="py-6 text-center"><Loader2 className="inline h-5 w-5 animate-spin text-slate-400" /></div> : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            {Object.entries(SEV_META).map(([k, m]) => (
              <div key={k} className={`rounded-xl px-3 py-2 ring-1 ${m.cls}`} data-testid={`ops-count-${k}`}>
                <div className="text-xl font-bold tabular-nums">{data.counts?.[k] || 0}</div>
                <div className="text-[11px] font-medium">{m.label}</div>
              </div>
            ))}
          </div>
          <div className="mt-3 space-y-2">
            {top.map((r) => <RiskRow key={r.id} r={r} compact />)}
            {top.length === 0 && <div className="py-3 text-sm text-slate-500" data-testid="ops-briefing-clear">No operational issues detected right now.</div>}
          </div>
          <Link to="/ops-briefing" className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700" data-testid="ops-briefing-open">
            Open full briefing <ArrowRight className="h-4 w-4" />
          </Link>
        </>
      )}
    </div>
  );
}
